import { useState, useRef, useCallback } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import {
  Result,
  ResultType,
} from "~/react/components/multi_domain_search/result"
import useIsInView from "~/react/hooks/use-is-in-view"

const estimatedSize = 80

// Department Section Component
export interface DepartmentSectionProps {
  key: string
  department: string
  title: string
  results: ResultType[]
  hideCompanyDetails?: boolean
  selectedIds: number[]
  onSelect: (ids: number[]) => void
  handleReveal: (id: number) => void
  handleLeadsListSave: (name: string, listId: number, emailId: number) => void
  handleCampaignsSave: (
    name: string,
    campaignId: number,
    emailId: number,
  ) => void
  leadsLists: { id: number; name: string }[]
  campaigns: { id: number; name: string }[]
  defaultExpanded: boolean
  clickToCopy?: boolean
}

export const DepartmentSection: React.FC<DepartmentSectionProps> = ({
  department,
  title,
  results,
  hideCompanyDetails,
  selectedIds,
  onSelect,
  handleReveal,
  handleLeadsListSave,
  handleCampaignsSave,
  leadsLists,
  campaigns,
  defaultExpanded,
  clickToCopy = false,
}) => {
  if (results.length === 0) {
    return null
  }

  const [isExpanded, setIsExpanded] = useState(defaultExpanded) // Start expanded
  const departmentListRef = useRef(null)
  const isInView = useIsInView(departmentListRef)

  const handleSelectAll = () => {
    const departmentIds = results.map((r) => r.id)
    const allSelected = departmentIds.every((id) => selectedIds.includes(id))

    if (allSelected) {
      // Deselect all from this department
      onSelect(departmentIds)
    } else {
      // Select all from this department
      onSelect(departmentIds.filter((id) => !selectedIds.includes(id)))
    }
  }

  const allSelected =
    results.length > 0 && results.every((r) => selectedIds.includes(r.id))
  const someSelected =
    results.some((r) => selectedIds.includes(r.id)) && !allSelected

  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => departmentListRef.current,
    estimateSize: useCallback(() => estimatedSize, []),
    enabled: isExpanded && isInView,
  })
  const containerHeight = Math.min(virtualizer.getTotalSize(), 656)

  return (
    <section className="ds-section" data-testid={`department-${department}`}>
      <header className="ds-section__header">
        <input
          className="h-checkbox ds-section__checkbox"
          type="checkbox"
          id={`select-department-${department}`}
          checked={allSelected}
          ref={(input) => {
            if (input) input.indeterminate = someSelected
          }}
          onChange={handleSelectAll}
          title="Select all results from this department"
        />
        <button
          className="ds-section__toggle"
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          <span
            className={`ds-section__icon h-icon h-icon--sm fas fa-caret-${isExpanded ? "down" : "right"}`}
          ></span>
        </button>
        <h3 className="ds-section__title">
          {title}
          <span className="ds-section__count">{results.length}</span>
        </h3>
      </header>
      <div
        className={`ds-section__content ${!isExpanded ? "d-none" : ""}`}
        id={`ds-section-${department}`}
        ref={departmentListRef}
        style={{ height: `${containerHeight + 1}px`, overflow: "auto" }}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <Result
                key={virtualItem.index}
                result={results[virtualItem.index]}
                hideCompanyDetails={hideCompanyDetails}
                selected={selectedIds.includes(results[virtualItem.index]?.id)}
                leadsLists={leadsLists}
                campaigns={campaigns}
                onSelect={onSelect}
                handleReveal={handleReveal}
                handleLeadsListSave={handleLeadsListSave}
                handleCampaignsSave={handleCampaignsSave}
                isLast={virtualItem.index === results.length - 1}
                clickToCopy={clickToCopy}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
